import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import * as SentryAngular from '@sentry/angular';
import { init as initCapacitor } from '@sentry/capacitor';
import { AppConfigService } from 'src/app/app-config/app-config.service';
import { TerminalError } from 'src/app/payment/terminal-error';
import { environment } from '../../../environments/environment';
import { AppConfigurationError } from '../../app-config/app-configuration-error';
import { FailedPaymentError } from '../../payment/failed-payment-error';
import { ErrorNotificationsService } from './error-notifications.service';
import { SerializerError } from '../../data/converters/serializer';
import {PaymentApiError} from '../../data/pos-api-payment.service';
import {BackendServiceError} from '../../data/backend.service';


const SENTRY_ENABLED = environment.sentry.dsn !== '';

if (SENTRY_ENABLED) {
  const config = {
    dsn: environment.sentry.dsn,
    release: environment.sentry.release,
    environment: environment.production ? 'production' : 'development',
    ignoreErrors: [
      'Non-Error exception captured'
    ],
    tracesSampleRate: environment.production ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  };
  const dist = environment.sentry.dist ? { dist: environment.sentry.dist } : {};
  initCapacitor({ ...config, ...dist });
}

function getWrappedError(error: any): Error {
  if (error.rejection) {
    return getWrappedError(error.rejection);
  }
  if (error instanceof BackendServiceError) {
    if (error.wrapped instanceof HttpErrorResponse) {
      return new Error(error.wrapped.message);
    }
    if (error.wrapped instanceof SerializerError) {
      return new Error(
        `Serialization of field '${error.wrapped.getFieldName()}' failed (expected type: '${error.wrapped.getExpectedType()}', got: '${error.wrapped.getFieldType()}'.)`
      );
    }
  }
  if (error instanceof HttpErrorResponse) {
    return new Error(error.message);
  }
  if (error instanceof SerializerError) {
    return new Error(
      `Serialization of field '${error.getFieldName()}' failed (expected type: '${error.getExpectedType()}', got: '${error.getFieldType()}'.)`
    );
  }
  if (error.message) {
    return error;
  }

  let message = 'Unknown error';
  try {
    message += (': ' + JSON.stringify(error));
  }
  catch (e) {
    message += ': Can not be serialized';
  }
  return new Error(message);
}


@Injectable()
export class AppErrorHandler implements ErrorHandler {
  constructor(private readonly ngZone: NgZone,
    private readonly router: Router,
    private readonly errorNotifications: ErrorNotificationsService,
    private readonly configService: AppConfigService) {
  }

  handleError(error: any) {
    if (!SENTRY_ENABLED) {
      console.error(error);
    }

    this.ngZone.run(() => {
      if (SENTRY_ENABLED) {
        const sentryError = getWrappedError(error);
        SentryAngular.captureException(sentryError);
      }

      if (error instanceof AppConfigurationError || error?.rejection instanceof AppConfigurationError) {
        this.router.navigate(['/config-error']);
        return;
      }

      if (error instanceof PaymentApiError) {

        if (error.error instanceof HttpErrorResponse) {
          this.errorNotifications.showError(error.error?.error?.error?.human_message);
        }
        else {
          this.errorNotifications.showError(error.message);
        }
        return;
      }

      if (error.rejection instanceof PaymentApiError) {

        if (error.rejection.error instanceof HttpErrorResponse) {
          this.errorNotifications.showError(error.rejection.error?.error?.error?.human_message);
        }
        else {
          this.errorNotifications.showError(error.message);
        }
        return;
      }

      if (error instanceof FailedPaymentError || error?.rejection instanceof FailedPaymentError) {
        this.errorNotifications.showFailedPaymentError(error.rejection?.message || error.message);
        return;
      }

      if (error instanceof TerminalError || error?.rejection instanceof TerminalError) {
        const message = $localize`Die Zahlung konnte nicht durchgeführt werden.`;
        this.errorNotifications.showError(message);
        return;
      }

      if (error instanceof BackendServiceError) {
        this.errorNotifications.showError(error.endpointErrorsDescription);
        return;
      }
      if (error?.rejection instanceof BackendServiceError) {
        this.errorNotifications.showError(error?.rejection.endpointErrorsDescription);
        return;
      }

      let genericError: Error;
      if (this.configService.isConfigured() && this.configService.getConfig()?.debug !== false) {
        genericError = error;
      } else {
        genericError = new Error('Entschuldigung, etwas ist schiefgelaufen. Wenn dieser Fehler erneut auftritt, helfen Sie gerne unser Support-Team.');
      }
      this.errorNotifications.showGenericError(genericError);
    });
  }
}
